import { h, Fragment, render } from 'preact'
import { useState, useEffect } from 'preact/hooks'
import { createPortal } from 'preact/compat'
import { createModel, createUrl } from './utility/model'
import { single, multiple } from './utility/url'
import { json } from './utility/dom'

import { Loading, Query, SiteSettings } from './context'
import { usePrevious } from './hooks/usePrevious.js'
import { Search } from './components/Search.jsx'
import { Facets } from './components/Facets.jsx'
import { FilterTags } from './components/FilterTags.jsx'
import { Results } from './components/Results.jsx'
import { Showing } from './components/Showing.jsx'


export default function init() {
	const elements = {
		searchEl : document.getElementById('search-keywords'),
		filterTagsEl : document.getElementById('search-filter-tags'),
		facetsEl : document.getElementById('search-facets'),
		showingEl : document.getElementById('search-showing'),
		resultsEl : document.getElementById('search-results'),
	}
	if (!elements.searchEl) return

	const siteSettings = json('#site-settings')

	const query = createModel({
		initial : true,
		search : single('search'),
		region : multiple('region'),
		city : multiple('city'),
		category : multiple('category'),
		department : multiple('department'),
		timeType : multiple('timeType'),
		employmentType : multiple('employmentType'),
		remote : single('remote'),
		orderBy : single('orderBy'),
		perPage : single('perPage') || 25,
		page : single('page')
	})

	// empty out any currently rendered elements on the page
	for (const element in elements) {
		elements[element].innerHTML = ''
	}

	// render out the app
	render(
		<App query={query} siteSettings={siteSettings} {...elements} />, 
		document.body
	)
}


function App({ searchEl, filterTagsEl, resultsEl, facetsEl, showingEl, query, siteSettings }) {

	let latestReq = null
	const request = () => {
		const urlParams = { 
			...query.getAll(), 
			initial: undefined,
			lastEdited: undefined
		}
		const api = createUrl(siteSettings.homeUrl + '/api/jobs', urlParams, true)
		const search = createUrl(siteSettings.searchUrl, urlParams, false)
		latestReq = api
		setLoading(true)
		
		fetch(api).then(response => response.json()).then(response => {
			if (latestReq !== api) return;
			query.set('initial', false)
			setLoading(false)
			setResults(response.data ?? [])
			setPagination(response.pagination ?? {})
			setFacets(response.facets ?? {})
		})
		window.history.replaceState({}, '', search)
	}

	const [results, setResults] = useState([])
	const [facets, setFacets] = useState({})
	const previousFacets = usePrevious(facets, {})
	const mergedFacets = mergeFacets(query, facets, previousFacets, query.get('lastEdited'))

	const [pagination, setPagination] = useState({})
	const [loading, setLoading] = useState(false)

	// first load
	useEffect(request, [])

	return <>
		<SiteSettings.Provider value={{}}>
			<Query.Provider value={query}>
				<Loading.Provider value={loading}>
					{createPortal(
						<Search onUpdate={request} />, 
						searchEl
					)}
					{createPortal(
						<FilterTags 
							facets={mergedFacets}
							onUpdate={request} 
						/>, 
						filterTagsEl
					)}
					{createPortal(
						<Facets 
							facets={mergedFacets}
							onUpdate={request} 
						/>, 
						facetsEl
					)}
					{createPortal(
						<Results 
							results={results} 
							pagination={pagination} 
							onUpdate={request} 
						/>, 
						resultsEl
					)}
					{createPortal(
						<Showing pagination={pagination} />, 
						showingEl
					)}
				</Loading.Provider>
			</Query.Provider>
		</SiteSettings.Provider>
	</>
}

function mergeFacets(query, facets, previousFacets, lastEdit) {

	// disjunctive (OR) facets
	// remembers the previously-set facets, and copies across the max count, if the lastEdit key is the same
	// this allows selecting multiple facets within the same group without altering the group as you change selection
	if (lastEdit && facets[lastEdit]) {
		Object.keys(previousFacets[lastEdit].values ?? {}).map(key => {
			if (facets[lastEdit].values[key] === undefined) {
				facets[lastEdit].values[key] = previousFacets[lastEdit].values[key]
			} else {
				const previousCount = previousFacets[lastEdit].values[key].count
				const currentCount = facets[lastEdit].values[key].count
				facets[lastEdit].values[key] = {
					value: previousFacets[lastEdit].values[key].value,
					count: Math.max(previousCount, currentCount)
				} 
			}
		})
	}

	// remember selected facets so they don't disappear when editing other facets
	const queryParams = query.getAll()
	Object.entries(facets).map(([key, facet]) => {
		const params = queryParams[key]
		if (facet.type === 'list' && params.length > 0) {
			params.map(param => {
				// add it to the facets value array with count 0
				if (!facets[key].values[param]) {
					facets[key].values[param] = {
						value: param, 
						selected: true,
						count: 0
					}
				} else {
					facets[key].values[param].selected = true
				}
			})
		}
		if (facet.type === 'boolean' && params && params !== '') {
			// add it to the facets value array with count 0
			if (!facets[key].values['TRUE']) {
				facets[key].values['TRUE'] = {
					value: 'TRUE',
					count: 0
				}	
			} else {
				facets[key].values['TRUE'].selected = true
			}
		}
	})

	// order all facets values by object key
	for (const key in facets) {
		const unordered = facets[key].values
		const ordered = Object.keys(unordered).sort().reduce((obj, key) => {
		    obj[key] = unordered[key]
		    return obj
		}, {});
		facets[key].values = ordered
	}

	return facets
}